import { ServiceRequest } from "@features/cx/consumerManagement/types";
import { Badge } from "@shared/ui/badge";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  CheckCircle,
  ClipboardList,
  Clock,
  FileText,
  MessageSquare,
  Pause,
  Power,
  Settings,
  Wrench,
} from "lucide-react";
import { RequestHeaderProps } from "../types/requestHeaderProps";
import { CustomTooltip } from "@shared/components/CustomTooltip";
import { PageHeader } from "@shared/components/PageHeader";
import { useNavigate } from "react-router-dom";

export function RequestHeader({
  request,
  currentStatus = "pending",
  action,
  children,
}: RequestHeaderProps) {
  const navigate = useNavigate();

  const getStatusIcon = (status: ServiceRequest["statusDisplay"] | string) => {
    switch (status) {
      case "approved":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "rejected":
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case "pending":
        return <Clock className="h-4 w-4 text-amber-500" />;
      case "submitted":
        return <FileText className="h-4 w-4 text-blue-500" />;
      case "draft":
        return <FileText className="h-4 w-4 text-gray-500" />;
      case "completed":
        return <CheckCircle className="h-4 w-4 text-green-700" />;
      case "cancelled":
        return <AlertTriangle className="h-4 w-4 text-gray-500" />;
      case "in_progress":
        return <Clock className="h-4 w-4 text-blue-500" />;
      case "on_hold":
        return <Pause className="h-4 w-4 text-orange-500" />;
      default:
        return null;
    }
  };


  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex flex-col items-start w-full">
          <PageHeader
            title={`${action} Request Details`}
            subtitle={`Access all ${action?.toLowerCase()}-related information, progress and resolution details in one place.`}
            icon={ArrowLeft}
            onIconClick={() => navigate(-1)}
          />
        </div>
      </div>

      <div className="border rounded-lg bg-white shadow-sm overflow-hidden">
        <div className="flex justify-between items-start p-6">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center shadow-sm">
              <span className="text-lg font-semibold text-blue-700">
                {request?.name?.charAt(0)?.toUpperCase() || "R"}
              </span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="page-subheading">{request.name}</p>
              </div>
              <p className="text-muted-foreground">
                {action} Id: {request.requestNo ?? "NA"}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <Badge
                  className={`flex items-center gap-1 ${getStatusBadgeClass(request.priorityDisplay)}`}
                  variant="outline"
                >
                  {request.priorityDisplay ?? "NA"}
                </Badge>
                <Badge
                  variant="outline"
                  className={`flex items-center gap-1 capitalize ${getStatusBadgeClass(currentStatus)}`}
                >
                  {getStatusIcon(currentStatus)}
                  {currentStatus}
                </Badge>
              </div>
            </div>
          </div>

          {/* SLA Information in 3 columns */}
          {request.sla && (
            <div className="grid grid-cols-3 gap-4 border rounded-xl bg-white p-4 shadow-sm">
              <div className="text-center px-2">
                <p className="text-xs text-muted-foreground flex justify-center items-center gap-2">
                  SLA Status{" "}
                  <CustomTooltip
                    iconSize={14}
                    message="Shows 'On Track' if all SLA rules are met, otherwise 'Breached'."
                  />{" "}
                </p>
                <p className="font-medium flex items-center justify-center">
                  <span
                    className={`h-2 w-2 rounded-full mr-1 ${
                      request.sla.status === "On Track"
                        ? "bg-green-500"
                        : "bg-red-500"
                    }`}
                  ></span>
                  <p className="font-medium text-sm">
                    {request.sla.status || "NA"}
                  </p>
                </p>
              </div>
              <div className="text-center border-x border-gray-100 px-2">
                <p className="text-xs text-muted-foreground flex items-center gap-2">
                  SLA Due Date{" "}
                  <CustomTooltip
                    iconSize={14}
                    message="Deadline calculated from creation date plus defined resolution time."
                  />{" "}
                </p>
                <p className="font-medium text-sm">
                  {request.sla.dueDate || "NA"}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground flex items-center gap-2">
                  Time Remaining{" "}
                  <CustomTooltip
                    iconSize={14}
                    message="Time left until SLA due date, based on current time."
                  />{" "}
                </p>
                <p className="font-medium text-sm">
                  {request.sla.remaining || "NA"}
                </p>
              </div>
            </div>
          )}
        </div>
        {children && <div className="border-t p-5">{children}</div>}
      </div>
    </div>
  );
}
